import Keycloak, { KeycloakTokenParsed } from "keycloak-js";

const url = "keycloak.json";
// For some reason, we can't call this constructor...
// https://stackoverflow.com/questions/74799344/how-to-handle-error-new-expression-whose-target-lacks-a-construct-signature
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const kc = new (Keycloak as any)(url);

export interface ParsedToken extends KeycloakTokenParsed {
    preferred_username: string;
    groups: string[];
    iss: string;
    azp: string;
}

const doLogin = kc.login;
const doLogout = kc.logout;
const getToken = (): string | undefined => kc.token;
const getParsedToken = (): ParsedToken | undefined => kc.tokenParsed as ParsedToken | undefined;

/**
 * Automatically refresh the access token once it expires.
 */
kc.onTokenExpired = () => async (successCallback?: () => void) => {
    try {
        await kc.updateToken(5);
        successCallback && successCallback();
    } catch (e) {
        await doLogin();
        throw e;
    }
};

// Workaround because the refresh method above does not work...
setInterval(async () => {
    try {
        await kc.updateToken(30);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Refreshing access token failed", e);
    }
}, 5000);

/**
 * This callback is executed if the user is logged out instantaneously, e.g. because the cookies
 * are cleared.
 */
// kc.onAuthLogout = () => console.log("User has been logged out (Cookies cleared?)");

/**
 * Sets the callback that is executed once the authentication has been successful.
 *
 * @param callback The callback to execute
 */
const setAuthSuccessCallback = (callback: () => void): void => {
    kc.onAuthSuccess = callback;
};

/**
 * Sets the callback that is executed once the token refresh has been successful.
 *
 * @param callback The callback to execute
 */
const setAuthRefreshSuccessCallback = (callback: () => void): void => {
    kc.onAuthRefreshSuccess = callback;
};

/**
 * Initializes Keycloak instance and calls the provided callback function if the authentication
 * has been successful. Else it will forward the user to the login page.
 *
 * @param onAuthenticatedCallback
 */
const initKeycloak = async (onAuthenticatedCallback: () => void): Promise<void> => {
    try {
        const authenticated = await kc.init({
            // If the user is logged out (e.g. if cookies are cleared in devtools), an instant
            // redirect will happen. Maybe store the changed user data before? This would be
            // possible in the callback kc.onAuthLogout. However, it is not clear if this is
            // required at all.
            checkLoginIframe: false,
            onLoad: "login-required",
            pkceMethod: "S256"
        });
        if (authenticated) {
            onAuthenticatedCallback();
        } else {
            await doLogin();
        }
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Initializing keycloak failed", e);
    }
};

/**
 * Returns all roles that the user has for the current client.
 */
const getRolesForCurrentClient = (): string[] => {
    const resourcesAccess = getParsedToken()?.resource_access;
    return resourcesAccess ? resourcesAccess[kc.clientId || ""]?.roles : [];
};

const exports = {
    initKeycloak,
    doLogin,
    doLogout,
    getToken,
    getParsedToken,
    getRolesForCurrentClient,
    setAuthRefreshSuccessCallback,
    setAuthSuccessCallback
};

export default exports;
